import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Organisation } from "../../common/organisation";
import { OrganisationService } from "../../organisation/organisation.service";
import { FormControl } from "@angular/forms";
import { Subscription, debounceTime, distinctUntilChanged } from "rxjs";
import { SCREEN } from "../layouts-enums";
import { HelpGuide, HelpGuideSummary } from "../../common/help-model";
import { HelpGuidesService, matchGuides, organisationInUrl } from "../../help/guides/help-guides.service";
import { FunctionService } from "../../model-services/function.service";
import { HelpService } from "../../help/help-services";

// searches below this length only match titles, summaries and keywords, as the server's search needs whole words
const SEARCH_MIN_CHARS = 3
const SEARCH_DELAY_MS = 300

interface Category {
    name:string
    guides:HelpGuideSummary[]
}

interface Result {
    guide:HelpGuideSummary
    // the words around where the guide mentions what was searched for, when it was found in its text
    snippet?:string
}

// the help library: every guide, grouped by category, which can be searched, and the guide that's open. The guides are
// held on the server, and each is fetched when it's opened
@Component({
    selector: 'app-help-library',
    templateUrl: './help-library.html',
    styleUrls: ['./help-library.scss'],
    standalone: false,
})
export class HelpLibraryComponent implements OnInit, OnDestroy {
    screen = SCREEN.HELP
    guides:HelpGuideSummary[] = []
    categories:Category[] = []
    loaded = false
    failed = false

    search = new FormControl('', {nonNullable: true})
    results?:Result[]
    searching = false

    slug?:string
    guide?:HelpGuide
    guideLoading = false
    guideMissing = false

    @ViewChild('article', {read: ElementRef}) article?:ElementRef<HTMLElement>

    // the organisation the library is opened in, whose menu it keeps, if any
    organisation?:Organisation
    private base:string[] = ['/help']

    private subs:Subscription[] = []
    private searchCount = 0

    constructor(private route:ActivatedRoute, private router:Router, private guidesService:HelpGuidesService, private functionService:FunctionService,
        private helpService:HelpService, private organisationService:OrganisationService) {}

    async ngOnInit() {
        // a guide opened from inside an organisation (from the menu, a link in a guide or LINQ AI) stays in it
        const from = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString()
            ?? this.router.lastSuccessfulNavigation()?.previousNavigation?.finalUrl?.toString()
        const fromOrganisation = organisationInUrl(from)
        this.subs.push(this.route.params.subscribe(async p => {
            const key = p['organisation_key']
            if (!key && fromOrganisation) {
                this.router.navigate(['/organisations', fromOrganisation, 'help', ...(p['slug'] ? [p['slug']] : [])], {replaceUrl: true})
                return
            }
            this.base = key ? ['/organisations', key, 'help'] : ['/help']
            if (key && this.organisation?._key !== key) {
                await this.organisationService.checkOrganisationDataForRoute(key)
                this.organisation = this.organisationService.organisation || undefined
            }
            this.openGuide(p['slug'])
        }))
        this.subs.push(this.search.valueChanges.subscribe(q => this.filter(q)))
        this.subs.push(this.search.valueChanges.pipe(debounceTime(SEARCH_DELAY_MS), distinctUntilChanged()).subscribe(q => this.searchText(q)))
        try {
            this.guides = await this.guidesService.guides()
            this.categories = this.guides.reduce((list:Category[], g) => {
                const category = list.find(c => c.name === g.category)
                if (category) category.guides.push(g)
                else list.push({name: g.category, guides: [g]})
                return list
            }, [])
        }catch {
            this.failed = true
        }
        this.loaded = true
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe())
    }

    handleFunctionEvent(event:any) {
        this.functionService.executeFunction(event.function, event.value)
    }

    askAI() {
        this.helpService.openHelpChat()
    }

    clearSearch() {
        this.search.setValue('')
    }

    // the guides whose title, summary or keywords match straight away, while the server looks through their text
    private filter(query:string) {
        if (!query.trim()) {
            this.results = undefined
            return
        }
        const found = matchGuides(this.guides, query)
        // what the server found stays at the top until its next answer
        const fromText = (this.results || []).filter(r => r.snippet && !found.includes(r.guide))
        this.results = [...found.map(guide => ({guide, snippet: this.results?.find(r => r.guide === guide)?.snippet})), ...fromText]
    }

    // the guides that mention what was searched for anywhere in them, best first, then any others whose titles match
    private async searchText(query:string) {
        const count = ++this.searchCount
        if (query.trim().length < SEARCH_MIN_CHARS) return
        this.searching = true
        try {
            const found = await this.guidesService.search(query)
            // a later search has started
            if (count !== this.searchCount) return
            const byText:Result[] = found.flatMap(f => {
                const guide = this.guides.find(g => g.slug === f.slug)
                return guide ? [{guide, snippet: f.snippet}] : []
            })
            const byTitle = matchGuides(this.guides, query).filter(g => !byText.some(r => r.guide === g)).map(guide => ({guide}))
            this.results = [...byText, ...byTitle]
        }catch {
            // the matches by title still show
        }finally {
            if (count === this.searchCount) this.searching = false
        }
    }

    private async openGuide(slug?:string) {
        this.slug = slug
        this.guide = undefined
        this.guideMissing = false
        if (!slug) return
        this.guideLoading = true
        try {
            const guide = await this.guidesService.guide(slug)
            // another guide was opened while this one loaded
            if (this.slug !== slug) return
            this.guide = guide
            // a guide opened from further down the page starts at its top
            setTimeout(() => {
                const article = this.article?.nativeElement
                if (article && article.getBoundingClientRect().top < 0) article.scrollIntoView({block: 'start'})
            })
        }catch {
            if (this.slug === slug) this.guideMissing = true
        }finally {
            if (this.slug === slug) this.guideLoading = false
        }
    }

    // the link to the library, or to a guide in it, in the organisation it's opened in
    link(slug?:string):string[] {
        return slug ? [...this.base, slug] : this.base
    }

    // the guides before and after the open one, in the library's order
    neighbour(step:1|-1):HelpGuideSummary|undefined {
        const index = this.guides.findIndex(g => g.slug === this.slug)
        return index < 0 ? undefined : this.guides[index + step]
    }

    videoUrl(guide:HelpGuide) {
        return guide.video ? `api/help/${guide.video}/video` : undefined
    }
}
